import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CreditCard, Sparkles, Receipt, Bot, Settings,
  Trophy, AlertTriangle, LogOut, Mail, Chrome, Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Transactions as TxnsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { BrandLogo } from '@/components/brand/BrandLogo';

// Phase 11 — feature availability matrix. Anything that involves personal
// data, external account integrations or premium functionality MUST be
// flagged `signedInOnly` so the sidebar renders a lock badge for demo
// accounts (and the page itself renders a "sign in to use" gate).
//
// `pausedForConsumers: true` — temporarily hidden from non-admins behind a
// SOON pill. Admins still see the surface as normal (so they can curate /
// maintain it). The page itself renders <PausedGate> with the same gate.
// We keep the link in the sidebar (greyed) so consumers know the feature
// exists and will return.
const PRIMARY_NAV = [
  { to: '/dashboard',     label: 'Dashboard',        icon: LayoutDashboard, testid: 'sidebar-nav-dashboard' },
  { to: '/cards',         label: 'My Cards',         icon: CreditCard,      testid: 'sidebar-nav-cards' },
  { to: '/recommend',     label: 'Recommend',        icon: Sparkles,        testid: 'sidebar-nav-recommend',    badge: 'Hero' },
  { to: '/transactions',  label: 'Transactions',     icon: Receipt,         testid: 'sidebar-nav-transactions', pausedForConsumers: true },
  { to: '/sync/gmail',    label: 'Gmail Sync',       icon: Mail,            testid: 'sidebar-nav-gmail-sync',   signedInOnly: true,  pausedForConsumers: true },
  { to: '/sync/extension', label: 'Chrome Extension', icon: Chrome,          testid: 'sidebar-nav-extension',    badge: 'Soon',  signedInOnly: true },
];

const INTEL_NAV = [
  { to: '/leakage', label: 'Leakage Center', icon: AlertTriangle, testid: 'sidebar-nav-leakage', pausedForConsumers: true },
  { to: '/milestones', label: 'Milestones', icon: Trophy, testid: 'sidebar-nav-milestones', pausedForConsumers: true },
];

const FOOTER_NAV = [
  { to: '/assistant', label: 'AI Assistant', icon: Bot, testid: 'sidebar-nav-assistant', pausedForConsumers: true },
  { to: '/settings', label: 'Settings', icon: Settings, testid: 'sidebar-nav-settings' },
];

const Section = ({ label, items, isDemo = false, isAdmin = false }) => (
  <div>
    {label && <div className="px-3 mt-5 mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/80">{label}</div>}
    <div className="space-y-0.5">
      {items.map((item) => {
        const Icon = item.icon;
        const showLock = isDemo && item.signedInOnly;
        const showSoon = item.pausedForConsumers && !isAdmin;
        return (
          <NavLink key={item.to} to={item.to} data-testid={item.testid}
            className={({ isActive }) => cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors group hover:bg-[hsl(var(--surface-2))]',
              isActive ? 'bg-[hsl(var(--surface-2))] text-foreground font-medium border-l-2 border-accent -ml-[2px] pl-[10px]' : 'text-foreground/70 hover:text-foreground',
              (showLock || showSoon) && 'opacity-60'
            )}>
            <Icon className="h-[18px] w-[18px]" />
            <span className="flex-1">{item.label}</span>
            {showSoon && (
              <span
                className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground border border-border bg-[hsl(var(--surface-2))] rounded-full px-1.5 py-0.5"
                data-testid={`${item.testid}-soon`}
                title="Coming soon"
              >
                Soon
              </span>
            )}
            {showLock && !showSoon && (
              <span
                className="text-muted-foreground/80"
                title="Available only for signed-in users"
                data-testid={`${item.testid}-lock`}
              >
                <Lock className="h-3 w-3" />
              </span>
            )}
            {item.badge && !showLock && !showSoon && (
              <span className="text-[9px] uppercase tracking-wider font-semibold text-accent border border-accent/30 bg-accent/5 rounded-full px-1.5 py-0.5">{item.badge}</span>
            )}
          </NavLink>
        );
      })}
    </div>
  </div>
);

// Live optimization score pulled from the transactions summary. Reuses the
// same backend formula as the Dashboard hero card so they always agree.
// Shows '—' until the user has at least one rewards-bearing transaction.
const OptimizationWidget = () => {
  const { user } = useAuth();
  const [score, setScore] = useState(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) { setScore(null); setCount(0); return; }
    TxnsApi.summary()
      .then((s) => {
        if (cancelled) return;
        setScore(s?.total?.optimization_score ?? null);
        setCount(s?.total?.count ?? 0);
      })
      .catch(() => { if (!cancelled) { setScore(null); setCount(0); } });
    return () => { cancelled = true; };
  }, [user?.id]);

  const pct = score == null ? 0 : Math.max(0, Math.min(100, Math.round(score)));
  const display = score == null ? '—' : String(pct);
  const tagline = score == null
    ? (count === 0 ? 'No transactions yet' : 'Computing…')
    : `From ${count} transaction${count === 1 ? '' : 's'}`;

  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--surface-2))] p-4" data-testid="sidebar-optimization-widget">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Optimization</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-display text-2xl font-semibold" data-testid="sidebar-optimization-score">{display}</span>
        {score != null && <span className="text-xs text-muted-foreground">/ 100</span>}
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-[hsl(var(--surface-3))] overflow-hidden">
        <div className="h-full bg-accent rounded-full transition-[width] duration-300" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-[10px] text-muted-foreground">{tagline}</div>
    </div>
  );
};

const SignOutRow = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const handleSignOut = () => {
    logout();
    navigate('/login', { replace: true });
  };
  return (
    <button
      onClick={handleSignOut}
      data-testid="sidebar-sign-out"
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground/70 hover:text-foreground hover:bg-[hsl(var(--surface-2))] transition-colors"
    >
      <LogOut className="h-[18px] w-[18px]" />
      <span className="flex-1 text-left">Sign out</span>
    </button>
  );
};

export const Sidebar = () => {
  const { user } = useAuth();
  const isDemo = !!user?.is_demo;
  const isAdmin = !!user?.is_admin;
  return (
    <aside className="hidden lg:flex flex-col w-[260px] shrink-0 border-r border-border bg-[hsl(var(--surface-1))] sticky top-0 h-screen">
      <div className="px-5 pt-6 pb-5">
        <NavLink to="/dashboard" className="block" data-testid="sidebar-logo-link">
          <BrandLogo height={48} testId="sidebar-brand" />
        </NavLink>
      </div>
      <nav className="flex-1 px-3 overflow-y-auto scrollbar-thin pb-4">
        <Section items={PRIMARY_NAV} isDemo={isDemo} isAdmin={isAdmin} />
        <Section label="Intelligence" items={INTEL_NAV} isDemo={isDemo} isAdmin={isAdmin} />
        <Section label="You" items={FOOTER_NAV} isDemo={isDemo} isAdmin={isAdmin} />
        <div className="mt-2">
          <SignOutRow />
        </div>
      </nav>
      <div className="p-4 border-t border-border">
        <OptimizationWidget />
      </div>
    </aside>
  );
};
